import wretch from "wretch";
import queryString from "wretch/addons/queryString";

const api = wretch(import.meta.env.VITE_ONLYCLICKS_API)
  .addon(queryString)
  .options({ credentials: "include" })
  .url("/api")
  .resolve((_) =>
    _.unauthorized(() => {
      window.location.assign("/login");
      return Promise.resolve();
    }),
  );

// const fetcher = <Response>() => {
//   return api
//     .options({ signal })
//     .url(url)
//     .query(query ?? {})
//     .get()
//     .json<Response>();
// };

export enum SignInProvider {
  Password = "password",
  Google = "google.com",
}

export interface Session {
  id: string;
  email: string;
  providers: SignInProvider[];
}

export const whoami = () => {
  return wretch(import.meta.env.VITE_ONLYCLICKS_API)
    .options({ credentials: "include" })
    .url("/api/sessions/whoami")
    .get()
    .json<Session>();
};

export interface Login {
  id_token: string;
}

export const postSessionLogin = (b: Login) => {
  return wretch(import.meta.env.VITE_ONLYCLICKS_API)
    .url("/api/sessions/login")
    .options({ credentials: "include" })
    .post(b)
    .res();
};

export const postSessionLogout = () => {
  return wretch(import.meta.env.VITE_ONLYCLICKS_API)
    .url("/api/sessions/logout")
    .options({ credentials: "include" })
    .post()
    .res();
};

export interface URL {
  url: string;
}

export interface PostCheckoutSessionRequest {
  plan_id: string;
}

export const postCheckoutSession = (b: PostCheckoutSessionRequest) => {
  return wretch(import.meta.env.VITE_ONLYCLICKS_API)
    .url("/api/sessions/checkout")
    .options({ credentials: "include" })
    .post(b)
    .json<URL>();
};

export const postCustomerPortalSession = () => {
  return wretch(import.meta.env.VITE_ONLYCLICKS_API)
    .url("/api/sessions/customer_portal")
    .options({ credentials: "include" })
    .post()
    .json<URL>();
};

export const deleteAccount = (id: string) => {
  return api.url(`/accounts/${id}`).delete().res();
};

export interface Limit {
  usage: number;
  limit: number;
}

export interface Entitlements {
  bikes: Limit;
  setups: Limit;
}

export const isEntitledTo = (limit: Limit): boolean => {
  return limit.usage < limit.limit;
};

export const getEntitlements = (signal: AbortSignal, accountId: string) => {
  return api
    .url(`/accounts/${accountId}/entitlements`)
    .options({ signal })
    .get()
    .json<Entitlements>();
};

export enum Plan {
  Free = "free",
  Paid = "paid",
}

export interface Subscription {
  id: string;
  plan_name: Plan;
  customer_id?: string;
}

export const getSubscription = (signal: AbortSignal, accountId: string) => {
  return api
    .url(`/accounts/${accountId}/subscription`)
    .options({ signal })
    .get()
    .json<Subscription>();
};

export interface Preferences {
  counting_from: CountingFrom;
  tire_pressure_unit: TirePressureUnit;
  selected_bike?: string;
}

export const getPreferences = (signal: AbortSignal, accountId: string) => {
  return api
    .url(`/accounts/${accountId}/preferences`)
    .options({ signal })
    .get()
    .json<Preferences>();
};

export const putPreferences = (accountId: string, preferences: Preferences) => {
  return api
    .url(`/accounts/${accountId}/preferences`)
    .put(preferences)
    .json<Preferences>();
};

export interface Paginated<I> {
  items: I[];
  next_page_token?: number;
}

export const CountingFromValues = ["fully_open", "fully_closed"] as const;
export type CountingFrom = (typeof CountingFromValues)[number];

export interface Bike {
  id: string;
  name: string;
  show_wheels: true;
  fork?: Fork;
  shock?: Shock;
  created_at: string;
}

export type SuspensionType = "air" | "coil";

export interface Fork {
  type: SuspensionType;
  show_sag: boolean;
  show_hsc: boolean;
  show_lsc: boolean;
  show_hsr: boolean;
  show_lsr: boolean;
  show_volume_spacers: boolean;
  show_negative_psi: boolean;
}

export interface Shock {
  type: SuspensionType;
  show_sag: boolean;
  show_hsc: boolean;
  show_lsc: boolean;
  show_hsr: boolean;
  show_lsr: boolean;
  show_volume_spacers: boolean;
}

export const listBikes = (signal: AbortSignal, pageSize?: number) => {
  return api
    .url("/bikes")
    .options({ signal })
    .query({ page_size: pageSize })
    .get()
    .json<Paginated<Bike>>();
};

export const getBike = (signal: AbortSignal, bikeId: string) => {
  return api.url(`/bikes/${bikeId}`).options({ signal }).get().json<Bike>();
};

export const postBike = (bike: Omit<Bike, "id" | "created_at">) => {
  return api.url("/bikes").post(bike).json<Bike>();
};

export interface PutBike {
  name: string;
  show_wheels: boolean;
  fork?: Fork;
  shock?: Shock;
}

export const putBike = (bikeId: string, bike: PutBike) => {
  return api.url(`/bikes/${bikeId}`).put(bike).json<Bike>();
};

export const deleteBike = (id: string) => {
  return api.url(`/bikes/${id}`).delete().res();
};

export interface Setup {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export const listSetups = (
  signal: AbortSignal,
  bikeId: string,
  q?: string,
  pageToken?: number,
) => {
  return api
    .url("/setups")
    .options({ signal })
    .query({
      bike_id: bikeId,
      q,
      page_size: 10,
      page_token: pageToken,
    })
    .get()
    .json<Paginated<Setup>>();
};

export const getSetup = (signal: AbortSignal, setupId: string) => {
  return api.url(`/setups/${setupId}`).options({ signal }).get().json<Setup>();
};

export const exportSetup = (setupId: string) => {
  return api.url(`/setups/${setupId}/export`).get().blob();
};

export const postSetup = (
  setup: Omit<Setup, "id" | "created_at" | "updated_at">,
) => {
  return api.url("/setups").post(setup).json<Setup>();
};

export const deleteSetup = (id: string) => {
  return api.url(`/setups/${id}`).delete().res();
};

export const cloneSetup = (
  setupId: string,
  setup: Omit<Setup, "id" | "created_at" | "updated_at">,
) => {
  return api.url(`/setups/${setupId}/clone`).post(setup).json<Setup>();
};

export interface Value<T> {
  curr?: T;
  prev?: T;
}

export interface ForkSettings {
  id: string;
  sag?: Value<number>;
  psi?: Value<number>;
  lbs?: Value<number>;
  lsc?: Value<number>;
  hsc?: Value<number>;
  lsr?: Value<number>;
  hsr?: Value<number>;
  volume_spacers?: Value<number>;
  negative_psi?: Value<number>;
  created_at: string;
}

export interface PostForkSettingsBody {
  sag?: number;
  psi?: number;
  lbs?: number;
  lsc?: number;
  hsc?: number;
  lsr?: number;
  hsr?: number;
  volume_spacers?: number;
  negative_psi?: number;
}

export interface ShockSettings {
  id: string;
  sag?: Value<number>;
  psi?: Value<number>;
  lbs?: Value<number>;
  lsc?: Value<number>;
  hsc?: Value<number>;
  lsr?: Value<number>;
  hsr?: Value<number>;
  volume_spacers?: Value<number>;
  created_at: string;
}

export interface PostShockSettingsBody {
  sag?: number;
  psi?: number;
  lbs?: number;
  lsc?: number;
  hsc?: number;
  lsr?: number;
  hsr?: number;
  volume_spacers?: number;
}

export const TirePressureUnits = ["bar", "psi"] as const;
export type TirePressureUnit = (typeof TirePressureUnits)[number];

export interface WheelsSettings {
  id: string;
  front_bar?: Value<number>;
  rear_bar?: Value<number>;
  front_psi?: Value<number>;
  rear_psi?: Value<number>;
  created_at: string;
}

export interface PostWheelsSettingsBody {
  tire_pressure_unit: TirePressureUnit;
  front_bar?: number;
  rear_bar?: number;
  front_psi?: number;
  rear_psi?: number;
}

export interface Settings {
  id: string;
  fork?: Omit<ForkSettings, "id" | "created_at">;
  shock?: Omit<ShockSettings, "id" | "created_at">;
  wheels?: Omit<WheelsSettings, "id" | "created_at">;
  notes?: Value<string>;
  created_at: string;
}

export const getSettings = (
  signal: AbortSignal,
  setupId: string,
  unit: TirePressureUnit,
  pageSize?: number,
  pageToken?: number,
) => {
  return api
    .url(`/setups/${setupId}/settings`)
    .options({ signal })
    .query({ unit, page_size: pageSize, page_token: pageToken })
    .get()
    .json<Paginated<Settings>>();
};

export interface PostSettingsBody {
  counting_from: CountingFrom;
  fork?: PostForkSettingsBody;
  shock?: PostShockSettingsBody;
  wheels?: PostWheelsSettingsBody;
  notes?: string;
}

export const postSettings = (setupId: string, b: PostSettingsBody) => {
  return api.url(`/setups/${setupId}/settings`).post(b).json<Settings>();
};
